import React, { useEffect, useState } from "react";
import { useTitle } from "../context/TitleContext";
import PermissionButton from "../components/PermissionButton";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { fetchSliderTexts, deleteSliderText, clearSliderTextState } from "../redux/Slices/SliderTextSlice";
import { toast } from "react-toastify";
import AddSliderText from "../components/SliderTexts/AddSliderText";
import EditSliderText from "../components/SliderTexts/EditSliderText";
import Pagination from '../components/Pagination';

const SliderTexts = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();

  const { data: sliderTexts, meta, loading, error, deleteLoading } = useSelector((state) => state.sliderTexts);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSliderText, setEditingSliderText] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingSliderText, setDeletingSliderText] = useState(null);

  useEffect(() => {
    setTitle("نصوص البانرات");
    dispatch(clearSliderTextState());
  }, [setTitle, dispatch]);

  useEffect(() => {
    dispatch(fetchSliderTexts(currentPage));
  }, [dispatch, currentPage]);

  const handleDelete = (sliderText) => {
    dispatch(clearSliderTextState());
    setDeletingSliderText(sliderText);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deletingSliderText) {
      dispatch(deleteSliderText(deletingSliderText.id));
      setShowDeleteModal(false);
      setDeletingSliderText(null);
    }
  };

  const handleEdit = (sliderText) => {
    dispatch(clearSliderTextState());
    setEditingSliderText(sliderText);
    setShowAddForm(false);
  };

  const handleCancelEdit = () => {
    setEditingSliderText(null);
  };

  const handlePageChange = (page) => {
    if (loading) return;
    if (page === currentPage) return;
    setCurrentPage(page);
  };



  useEffect(() => {
    if (error && error !== null) {
      toast.error(error);
      dispatch(clearSliderTextState());
    }
  }, [error, dispatch]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  if (loading && (!sliderTexts || sliderTexts.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .slider-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
        }
        .slider-table th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 14px 16px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          text-align: center !important;
        }
        .slider-table td {
          padding: 14px 16px !important;
          font-size: 14px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            📢 نصوص البانرات المتحركة (Slider Texts)
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل ومراقبة النصوص المتحركة في واجهة التطبيق والموقع الرئيسي</p>
        </div>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            setEditingSliderText(null);
          }}
          style={{
            display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14
          }}
        >
          {showAddForm ? "إخفاء النموذج" : "إضافة نص جديد"}
        </button>
      </div>

      {showAddForm && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: 24, marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
          <AddSliderText onSuccess={() => setShowAddForm(false)} />
        </div>
      )}

      {editingSliderText && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: 24, marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
          <EditSliderText sliderText={editingSliderText} onSuccess={handleCancelEdit} />
        </div>
      )}

      {/* ── Table Container ────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", padding: 0 }}>
        {(!sliderTexts || sliderTexts.length === 0) ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <i className="bi bi-chat-left-text display-4 text-muted"></i>
            <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد نصوص مسجلة حالياً</p>
          </div>
        ) : (
          <div style={{ padding: 0 }}>
            <div className="table-responsive">
              <table className="slider-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>النص العربي</th>
                    <th>النص الإنجليزي</th>
                    <th>الحالة</th>
                    <th>تاريخ الإنشاء</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {sliderTexts.map((sliderText, index) => (
                    <tr key={sliderText.id}>
                      <td>
                        {meta ? (meta.current_page - 1) * meta.per_page + index + 1 : index + 1}
                      </td>
                      <td style={{ fontWeight: 600, color: "#0f172a", maxWidth: 280, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                        {sliderText.text_ar}
                      </td>
                      <td style={{ color: "#475569", maxWidth: 280, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                        {sliderText.text_en}
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, color: (sliderText.status === 'active' || sliderText.status === 1 || sliderText.status === '1') ? "#15803d" : "#64748b", background: (sliderText.status === 'active' || sliderText.status === 1 || sliderText.status === '1') ? "#d1fae5" : "#f1f5f9", padding: "3px 10px", borderRadius: 20 }}>
                          {(sliderText.status === 'active' || sliderText.status === 1 || sliderText.status === '1') ? "نشط" : "غير نشط"}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, color: "#64748b" }}>{formatDate(sliderText.created_at)}</td>
                      <td>
                        <div style={{ display: "inline-flex", gap: 5, justifyContent: "center" }}>
                          <PermissionButton
                            onClick={() => handleEdit(sliderText)}
                            permission="edit-slider-texts"
                            title="تعديل"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaEdit size={14} />
                          </PermissionButton>
                          <PermissionButton
                            onClick={() => handleDelete(sliderText)}
                            permission="delete-slider-texts"
                            disabled={deleteLoading}
                            title="حذف"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaTrash size={14} />
                          </PermissionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "0 16px 20px" }}>
              <Pagination meta={meta} currentPage={currentPage} loading={loading} onChange={handlePageChange} />
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeletingSliderText(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف نص البانر التالي؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--red-color)" }}>
                💬 {deletingSliderText?.text_ar}
              </div>
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletingSliderText(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} disabled={deleteLoading} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                {deleteLoading ? <><span className="spinner-border spinner-border-sm"></span> جاري الحذف...</> : "حذف النص"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SliderTexts;